import { type ElementSize } from "../hooks/use-element-size.ts"

// The calendar grid already says when a meeting happens, how long it lasts,
// and which day/column it's in — but the card should still surface the
// event's own relevant fields (who/what the meeting is for, time) whenever
// the box has room, compressing the LAYOUT rather than deleting the data.
//
// Client escalation (EAD, 2026-09-23): the prior version of this file let
// time share the title's own row down to any 15/30-minute event ("type+time
// sharing one compact row even at 15/30 minutes, never hidden outright" —
// see that round's own comment, now reversed). In a narrow column
// (`CalendarTimeAndType`'s time text is `shrink-0` — it reserves its full
// width unconditionally, never yielding to its sibling) that meant the
// customer-identity headline this file's sibling decision added was the
// one that lost the width fight: `Lead Meeting: For Arc…` next to a
// half-legible `7:00a…`, in the Week view's narrower 7-day columns.
// Confirmed live against the exact production markup, not a guess — the
// same `shrink-0` sibling the client's own audit pointed at.
//
// The fix is a priority order, not a tooltip: customer identity is the
// content on a short card, and nothing may compete with it for horizontal
// width. Time now only ever gets a full line of its OWN (stacked below the
// title) once there is real height for one — it never again shares the
// title's row. A box too short for that stacked line just doesn't show a
// time at all; the grid's own time axis already answers "when".
//
// Every cutoff below keys off the rendered box, not `diffInMinutes`:
// FullCalendar's `eventMinHeight` (Calendar.tsx, a true 20px safety floor —
// see its own comment) and calendar.less' `@slot-height` are what make a
// real 15-minute event (~14px, floor-clamped to 20px) genuinely shorter
// than a real 30-minute one (28px, unclamped) — a duration cutoff could
// never express that (see the 2026-09-15 KB decision this whole file's
// history stems from).
const HEIGHT_FOR_STACKED_TIME = 32
const HEIGHT_FOR_ONE_CONTEXT_LINE = 48
const HEIGHT_FOR_TWO_CONTEXT_LINES = 70

// Width priority as the column narrows, widest requirement first (so it's
// the first thing to compress or disappear): the second context line, then
// the first, then the full time range collapsing to just its start, then
// the stacked time line itself. Title never loses this race — ellipsis (now
// paired with a hover tooltip) is its own last-resort safety net, never
// triggered by these cutoffs, and title is never asked to share a row with
// anything that would shrink it further.
const MIN_WIDTH_FOR_CONTEXT_LINE = 100
const MIN_WIDTH_FOR_TIME_RANGE = 110
// A narrow overlapping column has no room for a stacked time line's own
// legible width either — time disappears rather than squeezing onto a line
// too narrow to read, however tall the box is.
const MIN_WIDTH_FOR_STACKED_TIME = 90

export type TimePlacement = "stacked" | "hidden"
export type TimeContent = "full" | "short"

export interface EventCardTier {
  timePlacement: TimePlacement
  /** Only meaningful when `timePlacement !== "hidden"`. */
  timeContent: TimeContent
  showContext: boolean
  /** A second context line, past the first — real room to spare only. */
  showSecondContext: boolean
}

export function pickEventCardTier({
  width,
  height
}: ElementSize): EventCardTier {
  const canStackHeight = height >= HEIGHT_FOR_STACKED_TIME
  const canStackWidth = width >= MIN_WIDTH_FOR_STACKED_TIME
  const timePlacement: TimePlacement =
    canStackHeight && canStackWidth ? "stacked" : "hidden"

  const showContext =
    width >= MIN_WIDTH_FOR_CONTEXT_LINE && height >= HEIGHT_FOR_ONE_CONTEXT_LINE

  return {
    timePlacement,
    timeContent: width >= MIN_WIDTH_FOR_TIME_RANGE ? "full" : "short",
    showContext,
    // Rides on the first line existing at all — a box too short/narrow for
    // one context line never gets a second just because it's independently
    // tall (the height-for-two check is a superset of the height-for-one
    // check, so this only ever adds, never substitutes).
    showSecondContext: showContext && height >= HEIGHT_FOR_TWO_CONTEXT_LINES
  }
}

// The first half of `eventInfo.timeText` (FullCalendar's own formatted
// "11:00am - 11:30am"), used when there's only room for one time — showing
// when a meeting starts still answers "when is it" without the full
// range's width.
export function timeRangeStart(timeText: string): string {
  const [start] = timeText.split(/\s[-–]\s/)
  return (start || timeText).trim()
}

// calendar.less' `@slot-height` (28px per 30-minute slot — the grid's own
// pixel-per-minute scale, untouched) and Calendar.tsx's `eventMinHeight`
// floor, mirrored here for the one case a real measurement isn't available
// yet: initial mount, and jsdom in tests, which never runs layout at all.
const SLOT_HEIGHT_PX = 28
const SLOT_MINUTES = 30
const MIN_EVENT_HEIGHT_PX = 20

export function estimateEventHeightPx(durationMinutes: number): number {
  if (!Number.isFinite(durationMinutes)) return MIN_EVENT_HEIGHT_PX

  return Math.max(
    MIN_EVENT_HEIGHT_PX,
    (durationMinutes / SLOT_MINUTES) * SLOT_HEIGHT_PX
  )
}
